import { useDeferredValue, useMemo, useState } from 'react';
import type { ServiceMenuPageData, Service, ServiceCategory } from '@cng/shared';
import { useSite } from '@/app/page-data';
import { PageBranches } from '@/components/locations/PageBranches';
import { ServiceRow } from '@/components/services/ServiceRow';
import { ServiceLinks } from '@/components/services/ServiceLinks';
import { OfferStrip } from '@/components/services/OfferStrip';
import { AccentedTitle } from '@/components/common/AccentedTitle';

interface SectionData {
  key: string;
  title: string;
  titleAccent: string;
  note: string;
  services: Service[];
}

/** Everything a visitor might type against, lower-cased once per service. */
function haystack(service: Service, category: ServiceCategory): string {
  return [
    service.name,
    service.shortDescription,
    service.description,
    service.displayPriceText,
    service.variantLabel,
    category.name,
    ...(service.tags ?? []),
    ...(service.variants ?? []).map((v) => `${v.name} ${v.label}`),
  ].join(' ').toLowerCase();
}

/**
 * The men's / women's price menu with live search.
 *
 * The original filters `.search-item` rows with a `keyup` handler; this does the
 * same work in React, and additionally hides a category whose rows have all been
 * filtered out so the visitor never sees an empty heading.
 */
export function ServiceMenuPage({ data }: { data: ServiceMenuPageData & { kind: 'service-menu' } }) {
  const { settings } = useSite();
  const [query, setQuery] = useState('');
  // Typing stays responsive on the 144-row women's menu.
  const deferredQuery = useDeferredValue(query);

  const sections: SectionData[] = useMemo(
    () => data.categories.map(({ category, services }) => ({
      key: category._id,
      title: category.name,
      titleAccent: category.nameAccent,
      note: category.note,
      services,
    })),
    [data.categories],
  );

  const index = useMemo(() => {
    const map = new Map<string, string>();
    for (const { category, services } of data.categories) {
      for (const s of services) map.set(s._id, haystack(s, category));
    }
    return map;
  }, [data.categories]);

  const term = deferredQuery.trim().toLowerCase();

  const filtered = useMemo(() => {
    if (!term) return sections;
    return sections
      .map((section) => ({
        ...section,
        services: section.services.filter((s) => (index.get(s._id) ?? '').includes(term)),
      }))
      .filter((section) => section.services.length > 0);
  }, [sections, term, index]);

  const totalMatches = filtered.reduce((n, s) => n + s.services.length, 0);
  const page = data.page;
  const heroHeading = page.sections.find((s) => s.type === 'hero')?.data ?? {};
  const brandTitle = String(heroHeading.title ?? page.title);
  const brandSubtitle = String(heroHeading.subtitle ?? '');
  const gstNote = String(heroHeading.gstNote ?? '');

  /*
   * The men's and women's menus use identical class names for two different
   * designs, so each page's ported CSS is namespaced (see port-css.mjs) and the
   * matching scope class is applied here. They also differ in how the gold
   * highlight is named.
   */
  const scopeClass = data.audience === 'men' ? 'cng-menu-men' : 'cng-menu-women';
  const accentClass = data.audience === 'men' ? 'gold-accent' : 'gold-text';

  return (
    <>
    <div className={`luxury-breakout-container ${scopeClass}`}>
      <OfferStrip page={page} />

      <div className="search-hero">
        <h1 className="brand-title">{brandTitle}</h1>
        {brandSubtitle ? <p>{brandSubtitle}</p> : null}

        <div className="search-box-wrapper">
          <label className="sr-only" htmlFor="service-search">
            Search {data.audience === 'men' ? "men's" : "women's"} services
          </label>
          <input
            id="service-search"
            type="search"
            className="modern-search"
            placeholder={String(heroHeading.searchPlaceholder ?? 'Search for a service…')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
        </div>
      </div>

      <div className="content-wrapper">
        {/* Announced politely so a screen reader hears the result count as it changes. */}
        <p className="sr-only" role="status" aria-live="polite">
          {term
            ? `${totalMatches} service${totalMatches === 1 ? '' : 's'} match "${deferredQuery.trim()}"`
            : ''}
        </p>

        {filtered.length === 0 ? (
          <div id="noResultsMsg">No services found matching your search.</div>
        ) : (
          filtered.map((section) => (
            <div className="menu-category" key={section.key}>
              <div className="category-header">
                <AccentedTitle
                  as="h2"
                  className="category-title"
                  text={section.title}
                  accent={section.titleAccent}
                  accentClassName={accentClass}
                />
              </div>
              {section.note ? <p className="category-note">{section.note}</p> : null}
              {section.services.map((service) => (
                <ServiceRow key={service._id} service={service} settings={settings} />
              ))}
            </div>
          ))
        )}

        {gstNote ? <p className="gst-note">{gstNote}</p> : null}
      </div>
    </div>
    <ServiceLinks />
    <PageBranches sections={page.sections} />
    </>
  );
}
